import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/bi/Loading";
import { RefreshCw, CheckCircle2, XCircle, Clock } from "lucide-react";

const StatusPill = ({ status }) => {
  const ok = ["done", "sent", "success", "ok"].includes(String(status || "").toLowerCase());
  const running = String(status || "").toLowerCase() === "running";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full
      ${ok ? "bg-emerald-500/10 text-emerald-600" : running ? "bg-blue-500/10 text-blue-600" : "bg-amber-500/10 text-amber-600"}`}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : running ? <Clock className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {status || "—"}
    </span>
  );
};

const Panel = ({ title, children }) => (
  <Card className="glass-card border-0 p-4">
    <div className="text-[11px] font-semibold text-[color:var(--ink-2)] uppercase tracking-wide mb-2">{title}</div>
    {children}
  </Card>
);

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export function AdminRefreshMonitor() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/refresh-monitor").then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, []);
  if (!data) return <Loading label="Loading refresh monitor" />;
  if (data.error) return <div className="text-sm text-[color:var(--ink-3)]">Failed to load refresh monitor.</div>;
  return (
    <div className="space-y-4" data-testid="admin-refresh-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg text-[color:var(--ink)]">Refresh monitor</h3>
        <Button data-testid="refresh-monitor-reload" variant="outline" size="sm" onClick={load} className="h-8 text-xs bg-[color:var(--surface)]"><RefreshCw className="w-3.5 h-3.5 mr-1" />Reload</Button>
      </div>
      <Panel title={`Scheduled dataset refreshes (${(data.datasets || []).length})`}>
        {(data.datasets || []).length === 0 ? <div className="text-xs text-[color:var(--ink-3)]">No prepared datasets with refresh schedules.</div> : (
          <div className="space-y-1">
            {data.datasets.map((d) => (
              <div key={d.dataset_id} data-testid={`refresh-ds-${d.dataset_id}`} className="flex items-center justify-between text-xs bg-[color:var(--surface-2)] rounded-lg px-3 py-2 gap-2">
                <span className="truncate font-medium text-[color:var(--ink)]">{d.name}</span>
                <span className="flex items-center gap-3 shrink-0 text-[color:var(--ink-3)]">
                  <span>{d.enabled ? `every ${d.frequency || "?"}` : "manual"}</span>
                  <span>next: {d.enabled ? fmt(d.next_run_at) : "—"}</span>
                  <span>{d.row_count ?? "—"} rows</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </Panel>
      <Panel title={`Recent refresh jobs (${(data.jobs || []).length})`}>
        {(data.jobs || []).length === 0 ? <div className="text-xs text-[color:var(--ink-3)]">No refresh jobs yet.</div> : (
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {data.jobs.map((j) => (
              <div key={j.id} className="flex items-center justify-between text-xs bg-[color:var(--surface-2)] rounded-lg px-3 py-2 gap-2">
                <span className="truncate">{j.dataset_name || j.dataset_id} <span className="text-[color:var(--ink-3)]">({j.trigger})</span></span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className="text-[color:var(--ink-3)]">{fmt(j.started_at)}</span>
                  <StatusPill status={j.status} />
                </span>
              </div>
            ))}
          </div>
        )}
      </Panel>
      <Panel title={`Report delivery schedules (${(data.schedules || []).length})`}>
        {(data.schedules || []).length === 0 ? <div className="text-xs text-[color:var(--ink-3)]">No scheduled report deliveries.</div> : (
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {data.schedules.map((s) => (
              <div key={s.id} className="flex items-center justify-between text-xs bg-[color:var(--surface-2)] rounded-lg px-3 py-2 gap-2">
                <span className="truncate">{s.report_name || "Report"} <span className="text-[color:var(--ink-3)]">· {s.frequency} · {String(s.format || "pdf").toUpperCase()}</span></span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className="text-[color:var(--ink-3)]">next: {fmt(s.next_run_at)}</span>
                  {s.last_status && <StatusPill status={s.last_status} />}
                </span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
